import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Package, ShoppingBag, FileText, Truck, Wrench,
  HardHat, Wallet, Users, Star, MapPin, LogOut, Menu, X, CalendarDays, UserCog, Settings, Tag, Landmark, LifeBuoy, Megaphone, Newspaper, PencilRuler, MessageSquare, ScrollText, Zap,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useUnreadMessages from "@/hooks/useUnreadMessages";
import logoMark from "@/assets/logo-mark.png";

const LINKS = [
  { to: "/admin", icon: LayoutDashboard, label: "Tableau de bord", end: true, mod: "dashboard", roles: "all" },
  { to: "/admin/catalogue", icon: Package, label: "Catalogue", mod: "catalogue", roles: [] },
  { to: "/admin/pages", icon: FileText, label: "Pages & Contenus", mod: "pages", roles: [] },
  { to: "/admin/promotions", icon: Tag, label: "Promotions", mod: "promotions", roles: ["commercial"] },
  { to: "/admin/ventes-flash", icon: Zap, label: "Ventes Flash", mod: "promotions", roles: ["commercial"] },
  { to: "/admin/commandes", icon: ShoppingBag, label: "Commandes", mod: "commandes", roles: ["commercial", "comptable", "responsable_logistique"] },
  { to: "/admin/devis", icon: FileText, label: "Devis", mod: "devis", roles: ["commercial"] },
  { to: "/admin/projets", icon: PencilRuler, label: "Projets", mod: "projets", roles: ["commercial"] },
  { to: "/admin/livraisons", icon: Truck, label: "Livraisons", mod: "livraisons", roles: ["responsable_logistique"] },
  { to: "/admin/installations", icon: Wrench, label: "Installations", mod: "installations", roles: ["responsable_installation"] },
  { to: "/admin/planning", icon: CalendarDays, label: "Planning installations", mod: "planning", roles: ["responsable_installation"] },
  { to: "/admin/professionnels", icon: HardHat, label: "Professionnels", mod: "professionnels", roles: ["responsable_installation"] },
  { to: "/admin/paiements", icon: Wallet, label: "Paiements pros", mod: "paiements", roles: ["comptable"] },
  { to: "/admin/tresorerie", icon: Landmark, label: "Trésorerie", mod: "tresorerie", roles: ["comptable"] },
  { to: "/admin/clients", icon: Users, label: "Clients", mod: "clients", roles: ["commercial"] },
  { to: "/admin/avis", icon: Star, label: "Avis", mod: "avis", roles: ["commercial"] },
  { to: "/admin/support", icon: LifeBuoy, label: "Support", mod: "support", roles: ["commercial"] },
  { to: "/admin/messagerie", icon: MessageSquare, label: "Messagerie", mod: "messagerie", roles: ["commercial"] },
  { to: "/admin/influenceurs", icon: Megaphone, label: "Influenceurs", mod: "influenceurs", roles: [] },
  { to: "/admin/blog", icon: Newspaper, label: "Blog", mod: "blog", roles: [] },
  { to: "/admin/zones", icon: MapPin, label: "Zones", mod: "zones", roles: [] },
  { to: "/admin/utilisateurs", icon: UserCog, label: "Utilisateurs", mod: "utilisateurs", roles: [] },
  { to: "/admin/journal", icon: ScrollText, label: "Journal", mod: "journal", roles: [] },
  { to: "/admin/parametres", icon: Settings, label: "Paramètres", mod: "parametres", roles: [] },
];

export const STAFF_MODULES = [
  ["dashboard", "Tableau de bord"], ["catalogue", "Catalogue"], ["pages", "Pages & Contenus"], ["blog", "Blog"], ["promotions", "Promotions"], ["commandes", "Commandes"],
  ["devis", "Devis"], ["livraisons", "Livraisons"], ["installations", "Installations"], ["planning", "Planning"],
  ["professionnels", "Professionnels"], ["paiements", "Paiements pros"], ["tresorerie", "Trésorerie"], ["clients", "Clients"],
  ["avis", "Avis"], ["support", "Support"], ["influenceurs", "Influenceurs"], ["zones", "Zones"], ["utilisateurs", "Utilisateurs"], ["parametres", "Paramètres"], ["projets", "Projets"], ["messagerie", "Messagerie"], ["journal", "Journal"],
];

export default function AdminShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isActive = (l) => l.end ? location.pathname === l.to : location.pathname.startsWith(l.to);
  const msgUnread = useUnreadMessages();
  const isFullAdmin = ["admin", "super_admin"].includes(user?.role);
  const mods = user?.modules || [];
  const visibleLinks = LINKS.filter((l) => isFullAdmin || l.roles === "all" || (l.roles || []).includes(user?.role) || mods.includes(l.mod));

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 z-50 h-screen w-64 bg-[#0F172A] text-slate-300 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-5 py-4 flex items-center gap-2 border-b border-slate-700">
          <img src={logoMark} alt="" className="h-8 w-auto" />
          <span className="font-display font-extrabold text-lg text-white">TOGOBATI</span>
          <span className="text-[10px] bg-[#E08B00] text-[#0F172A] font-bold px-1.5 py-0.5 rounded ml-auto">ADMIN</span>
        </div>
        <nav className="flex-1 overflow-y-auto no-scrollbar py-3">
          {visibleLinks.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex items-center gap-3 px-5 py-2.5 text-sm font-medium transition ${isActive(l) ? "bg-[#C85A32] text-white" : "hover:bg-slate-800 hover:text-white"}`}>
              <l.icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /> {l.label}
              {l.mod === "messagerie" && msgUnread > 0 && <span className="ml-auto bg-[#DC2626] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="admin-nav-messages-badge">{msgUnread}</span>}
            </Link>
          ))}
        </nav>
        <div className="border-t border-slate-700 p-3">
          <p className="text-xs text-slate-400 px-2 truncate">{user?.name}</p>
          <button onClick={() => { logout(); navigate("/login"); }} className="mt-1 flex items-center gap-2 px-2 py-2 text-sm text-red-400 hover:bg-slate-800 rounded w-full" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu className="w-6 h-6" /></button>
          <span className="font-display font-extrabold text-lg">TOGOBATI Admin</span>
        </header>
        <main className="p-4 sm:p-6 max-w-7xl mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}

export function PageTitle({ children, action }) {
  return (
    <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0F172A]">{children}</h1>
      {action}
    </div>
  );
}

export function Panel({ children, className = "", ...rest }) {
  return <div className={`bg-white border border-slate-200 rounded-xl ${className}`} {...rest}>{children}</div>;
}
